import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Smartphone, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";

function relTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("de-CH", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function TrustedDevicesCard() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [busyAll, setBusyAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/auth/me/trusted-devices");
      setDevices(r.data || []);
    } catch (e) {
      toast.error("Geräte konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const revoke = async (id) => {
    if (!window.confirm("Dieses Gerät wirklich widerrufen? Beim nächsten Login wird wieder ein 2FA-Code verlangt.")) return;
    setBusyId(id);
    try {
      await apiClient.delete(`/auth/me/trusted-devices/${id}`);
      toast.success("Gerät widerrufen");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Widerruf fehlgeschlagen");
    } finally {
      setBusyId(null);
    }
  };

  const revokeAll = async () => {
    if (!devices.length) return;
    if (!window.confirm(`Alle ${devices.length} vertrauenswürdigen Geräte widerrufen?`)) return;
    setBusyAll(true);
    try {
      await apiClient.delete("/auth/me/trusted-devices");
      toast.success("Alle Geräte widerrufen");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Widerruf fehlgeschlagen");
    } finally {
      setBusyAll(false);
    }
  };

  return (
    <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="account-trusted-devices-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0">
            <Smartphone className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Vertrauenswürdige Geräte</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Geräte, die nach erfolgreichem 2FA-Login für 30 Tage gespeichert wurden. Auf diesen Geräten wird der Code-Schritt übersprungen.
            </p>
          </div>
        </div>
        {devices.length > 1 && (
          <Button
            variant="outline"
            size="sm"
            disabled={busyAll}
            onClick={revokeAll}
            className="text-rose-600 border-rose-200 hover:bg-rose-50"
            data-testid="account-revoke-all-devices-button"
          >
            {busyAll ? "Widerrufen …" : "Alle widerrufen"}
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-slate-500" data-testid="account-trusted-devices-loading">Lädt …</p>
      ) : devices.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500 text-center" data-testid="account-trusted-devices-empty">
          <AlertTriangle className="inline h-4 w-4 mr-1.5 text-amber-500" />
          Keine vertrauenswürdigen Geräte. Beim nächsten 2FA-Login kannst du dieses Gerät hinterlegen.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="account-trusted-devices-list">
          {devices.map((d) => (
            <li key={d.id} className="py-3 flex items-center gap-3" data-testid={`account-trusted-device-${d.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">
                  {d.device_label}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span>Erstellt: {relTime(d.created_at)}</span>
                  <span>Läuft ab: {relTime(d.expires_at)}</span>
                  {d.last_used_at && <span>Zuletzt genutzt: {relTime(d.last_used_at)}</span>}
                  {d.ip && <span className="text-slate-400">IP: {d.ip}</span>}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                disabled={busyId === d.id}
                onClick={() => revoke(d.id)}
                className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                data-testid={`account-revoke-device-button-${d.id}`}
                title="Gerät widerrufen"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
